import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/695dd613-14c2-4799-a475-715874e76069/images/e894b2566aa069a59cdc841ec443306053d3b591a28f3b91968ce3dedf7d2a06.png";

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      {/* Vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/80 to-slate-950" aria-hidden="true" />
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <p
            className="text-[11px] tracking-[0.2em] uppercase text-blue-400 mb-6"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            data-testid="hero-overline"
          >
            {t.hero.overline}
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 leading-[1.05]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="hero-title"
          >
            {t.hero.title}
          </h1>
          <p
            className="mt-7 text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#cta"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-slate-50 rounded-sm font-medium transition-all duration-300 shadow-[0_0_40px_rgba(37,99,235,0.3)]"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {t.hero.primaryCta}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-slate-700 hover:border-slate-500 text-slate-200 rounded-sm font-medium transition-colors duration-300"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {t.hero.secondaryCta}
            </a>
          </div>
        </div>

        {/* Metrics row */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 border-t border-slate-800">
          {[
            { v: t.hero.metric1, l: t.hero.metric1Label },
            { v: t.hero.metric2, l: t.hero.metric2Label },
            { v: t.hero.metric3, l: t.hero.metric3Label },
          ].map((m, i) => (
            <div
              key={i}
              className={`py-8 md:py-10 md:px-10 ${i !== 0 ? "md:border-l border-slate-800" : ""}`}
              data-testid={`hero-metric-${i}`}
            >
              <p
                className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {m.v}
              </p>
              <p
                className="mt-2 text-sm text-slate-400"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                {m.l}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
